/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { useFormik } from "formik";
import axios from "axios";
import * as Yup from "yup";
import {
  Typography,
  TextField,
  CircularProgress,
  FormHelperText,
  Box,
  Button,
  useTheme,
} from "@mui/material";
import DialogBox from "../../../_default/DialogBox";
import siteData from "../../../../_data";

// Reusable function to apply custom styles with the correct theme and fontsize
const useCustomStyles = (customfontsize, color) => ({
  text: {
    fontSize: `${customfontsize}px`,
    color,
    textAlign: "start",
    overflowWrap: "break-word",
    wordBreak: "normal",
    whiteSpace: "normal",
  },
  button: {
    border: `2px solid ${color}`,
    "&:hover": { border: `2px solid ${color}` },
    fontSize: `${customfontsize}px`,
  },
  input: {
    fontSize: `${customfontsize}px`,
    color,
    "& .MuiOutlinedInput-root": {
      fontSize: `${customfontsize}px`,
      "& fieldset": { borderColor: color },
      "&:hover fieldset": { borderColor: color },
      "&.Mui-focused fieldset": { borderColor: color },
    },
  },
  formHelper: {
    fontSize: `${customfontsize}px`,
    color: color,
  },
});

function Newsletter({ customfontsize }) {
  const {
    palette: { footer1 },
  } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);

  const customStyles = useCustomStyles(customfontsize, footer1.body);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "bold",
          textTransform: "uppercase",
          mb: 1,
          color: footer1.heading,
          fontSize: `${1.5 * customfontsize}px`,
        }}
      >
        Newsletter
      </Typography>

      <Typography variant="inherit" sx={customStyles.text}>
        {siteData.newsletter.description}
      </Typography>

      <TextField
        label="Email address"
        variant="outlined"
        fullWidth
        InputLabelProps={{
          sx: {
            color: footer1.body,
            fontSize: `${customfontsize}px`,
            "&.Mui-focused": { color: footer1.body },
          },
        }}
        FormHelperTextProps={{ sx: customStyles.formHelper }}
        sx={{ mt: 2, mb: 2, ...customStyles.input }}
        id="emailTmp"
        name="emailTmp"
        onClick={() => setOpen(true)}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        sx={customStyles.button}
      >
        Subscribe
      </Button>

      <DialogBox
        open={open}
        handleClose={handleClose}
        loading={loading}
        alt="Logout"
        src="/img/newsletter.jpg"
        heading="Subscribe to our Newsletter!"
        description={
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <NewsletterForm
              loading={loading}
              setLoading={setLoading}
              error={error}
              setError={setError}
              open={open}
              setOpen={setOpen}
              customfontsize={customfontsize}
            />
          </Box>
        }
      />
    </Box>
  );
}

function NewsletterForm({
  loading,
  setLoading,
  error,
  setError,
  open,
  setOpen,
  customfontsize,
}) {
  const { palette } = useTheme();
  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.post("/api/email", values);
        if (response.data.success) {
          resetForm();
        } else {
          setError(response.data.message);
        }
      } catch (error) {
        const errorMessage = error.response
          ? `Data from server: ${JSON.stringify(
              error.response.data
            )}\nStatus: ${error.response.status}\nHeaders: ${JSON.stringify(
              error.response.headers
            )}`
          : error.request
          ? "No response received for the request."
          : `Error during request setup: ${error.message}`;
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  const customStyles = useCustomStyles(customfontsize, palette.grey[600]);

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{ width: "100%", mt: 1 }}
    >
      <Typography variant="inherit" sx={customStyles.text}>
        {siteData.newsletter.description}
      </Typography>
      <TextField
        label="Email address"
        variant="outlined"
        fullWidth
        InputLabelProps={{
          sx: {
            fontSize: `${customfontsize}px`,
            "&.Mui-focused": { fontSize: `${customfontsize}px` },
          },
        }}
        FormHelperTextProps={{ sx: customStyles.formHelper }}
        id="email"
        name="email"
        onChange={(e) => {
          formik.handleChange(e);
          setError("");
        }}
        value={formik.values.email}
        disabled={loading}
        sx={{ mt: 2, mb: 2, ...customStyles.input }}
        error={formik.touched.email && Boolean(formik.errors.email)}
        helperText={formik.touched.email && formik.errors.email}
      />
      {error && (
        <FormHelperText
          sx={{
            mb: 2,
            color: "error.main",
            textAlign: "center",
            overflow: "hidden",
            fontSize: `${customfontsize}px`,
          }}
        >
          Error: {error}
        </FormHelperText>
      )}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={loading}
        sx={customStyles.button}
      >
        {formik.isSubmitting || loading ? (
          <CircularProgress size={24} sx={{ color: "white" }} />
        ) : (
          "Subscribe"
        )}
      </Button>
    </Box>
  );
}

export default Newsletter;
